import React, { useEffect, useRef } from 'react';
import { Panel, PanelGroup } from 'react-resizable-panels';
import { usePanelLayout } from './EditorLayoutContext';
import { FloatingWindow } from './FloatingWindow';
import { ResizeHandle, VerticalResizeHandle } from './ResizeHandles';

export const EditorLayout: React.FC<React.PropsWithChildren> = ({
  children
}) => {
  const {
    leftCollapsed,
    rightCollapsed,
    detachedPanels,
    draggingPanel,
    visibleLeftPanels,
    visibleRightPanels,
    hasVisibleLeftPanels,
    hasVisibleRightPanels,
    expandedPanel,
    layoutConfig,
    setLeftCollapsed,
    setRightCollapsed,
    setExpandedPanel,
    handleDetach,
    handleAttach,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp
  } = usePanelLayout();

  const leftPanelRef = useRef<HTMLDivElement>(null);
  const rightPanelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (expandedPanel) {
        const leftPanel = leftPanelRef.current;
        const rightPanel = rightPanelRef.current;
        const target = event.target as Node;

        const isOutsideLeft = leftPanel && !leftPanel.contains(target);
        const isOutsideRight = rightPanel && !rightPanel.contains(target);

        if (isOutsideLeft && isOutsideRight) {
          setExpandedPanel(null);
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [expandedPanel, setExpandedPanel]);

  const renderPanelContent = (id: string) => {
    const allPanels = [...layoutConfig.left, ...layoutConfig.right];
    const panel = allPanels.find((p) => p.id === id);
    if (!panel) return null;

    const PanelComponent = panel.component;
    return (
      <PanelComponent
        id={id}
        onDetach={handleDetach}
        isDetached={!!detachedPanels[id]}
      />
    );
  };

  return (
    <div
      className="h-full w-full relative"
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      <PanelGroup direction="horizontal">
        {hasVisibleLeftPanels && (
          <>
            <Panel
              defaultSize={20}
              collapsible
              collapsedSize={5}
              minSize={5}
              maxSize={30}
              onCollapse={() => {
                setLeftCollapsed(true);
                setExpandedPanel(null);
              }}
              onExpand={() => setLeftCollapsed(false)}
              className="bg-gray-50"
              id="left-sidebar"
            >
              <div className="h-full flex flex-col">
                <div className="flex-1 overflow-hidden">
                  {leftCollapsed ? (
                    <div className="py-2">
                      {visibleLeftPanels.map((id) => {
                        const panel = layoutConfig.left.find(
                          (p) => p.id === id
                        )!;
                        const Icon = panel.icon;
                        return (
                          <button
                            key={id}
                            className={`w-full p-2 hover:bg-gray-200 flex justify-center ${
                              expandedPanel === id ? 'bg-gray-200' : ''
                            }`}
                            onClick={() =>
                              setExpandedPanel(expandedPanel === id ? null : id)
                            }
                          >
                            <Icon className="w-5 h-5" />
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <PanelGroup direction="vertical">
                      {visibleLeftPanels.map((id, index) => {
                        const panel = layoutConfig.left.find(
                          (p) => p.id === id
                        )!;
                        return (
                          <React.Fragment key={id}>
                            {index > 0 && <VerticalResizeHandle />}
                            <Panel
                              defaultSize={panel.defaultSize}
                              minSize={panel.minSize}
                              maxSize={panel.maxSize}
                            >
                              {renderPanelContent(id)}
                            </Panel>
                          </React.Fragment>
                        );
                      })}
                    </PanelGroup>
                  )}
                </div>
              </div>
            </Panel>
            <ResizeHandle />
          </>
        )}

        <Panel className="bg-white relative" id={'map-editor-content'}>
          {children}
        </Panel>

        {hasVisibleRightPanels && (
          <>
            <ResizeHandle />
            <Panel
              defaultSize={20}
              collapsible
              collapsedSize={5}
              minSize={5}
              maxSize={30}
              onCollapse={() => {
                setRightCollapsed(true);
                setExpandedPanel(null);
              }}
              onExpand={() => setRightCollapsed(false)}
              className="bg-gray-50"
            >
              <div className="h-full flex flex-col">
                <div className="flex-1 overflow-hidden">
                  {rightCollapsed ? (
                    <div className="py-2">
                      {visibleRightPanels.map((id) => {
                        const panel = layoutConfig.right.find(
                          (p) => p.id === id
                        )!;
                        const Icon = panel.icon;
                        return (
                          <button
                            key={id}
                            className={`w-full p-2 hover:bg-gray-200 flex justify-center ${
                              expandedPanel === id ? 'bg-gray-200' : ''
                            }`}
                            onClick={() =>
                              setExpandedPanel(expandedPanel === id ? null : id)
                            }
                          >
                            <Icon className="w-5 h-5" />
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <PanelGroup direction="vertical">
                      {visibleRightPanels.map((id, index) => {
                        const panel = layoutConfig.right.find(
                          (p) => p.id === id
                        )!;
                        return (
                          <React.Fragment key={id}>
                            {index > 0 && <VerticalResizeHandle />}
                            <Panel
                              defaultSize={panel.defaultSize}
                              minSize={panel.minSize}
                              maxSize={panel.maxSize}
                            >
                              {renderPanelContent(id)}
                            </Panel>
                          </React.Fragment>
                        );
                      })}
                    </PanelGroup>
                  )}
                </div>
              </div>
            </Panel>
          </>
        )}
      </PanelGroup>

      {/* Floating Windows */}
      {Object.entries(detachedPanels).map(([id, panel]) => (
        <FloatingWindow
          key={id}
          id={id}
          position={panel.position}
          onClose={handleAttach}
          onMouseDown={(e) => handleMouseDown(e, id)}
          title={panel.title}
          icon={panel.icon}
          isDragging={draggingPanel === id}
        >
          {panel.content}
        </FloatingWindow>
      ))}

      {/* Expanded Panel Overlay */}
      {leftCollapsed && expandedPanel && (
        <div
          ref={leftPanelRef}
          className="fixed left-[5%] top-0 bottom-0 w-[300px] bg-white shadow-lg border-r border-gray-200"
          style={{ zIndex: 1000 }}
        >
          {renderPanelContent(expandedPanel)}
        </div>
      )}

      {/* Right Side Expanded Panel */}
      {rightCollapsed && expandedPanel && (
        <div
          ref={rightPanelRef}
          className="fixed right-[5%] top-0 bottom-0 w-[300px] bg-white shadow-lg border-l border-gray-200"
          style={{ zIndex: 1000 }}
        >
          {renderPanelContent(expandedPanel)}
        </div>
      )}
    </div>
  );
};
